if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.validateSessionBooking().then((valid) => {
          if (!valid) return;

          const guest = this.closest('product-info')?.querySelector('[data-session-guest]');
          if (guest) {
            const guestName = guest.value.trim();
            if (!guestName) {
              this.handleErrorMessage(guest.getAttribute('data-error') || '請填寫客人名稱');
              guest.focus();
              return;
            }
            guest.value = guestName;
          }

          const pack = this.closest('product-info')?.querySelector('tailor-pack');
          if (pack) {
            const choices = pack.querySelectorAll('[data-tailor-pack]');
            const selectedPack = Array.from(choices).find((el) => el.checked);
            if (!selectedPack) {
              this.handleErrorMessage(pack.getAttribute('data-error') || '請選擇包裝方式');
              pack.scrollIntoView({ block: 'nearest' });
              choices[0]?.focus();
              return;
            }
            if (typeof pack.sync === 'function') pack.sync();
          }

          const terms = this.form.querySelector('[data-session-terms]')
            || this.closest('product-info')?.querySelector('[data-session-terms]');
          if (terms && !terms.checked) {
            this.handleErrorMessage(terms.getAttribute('data-error') || '請先閱讀並同意條款');
            terms.focus();
            return;
          }

          this.handleErrorMessage();

          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
          this.querySelector('.loading__spinner').classList.remove('hidden');

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = new FormData(this.form);
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;

          fetch(`${routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
                this.handleErrorMessage(response.description);

                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (!soldOutMessage) return;
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
                return;
              } else if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
              }

              const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                }).then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                });
              this.error = false;
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      CartPerformance.measure("add:paint-updated-sections", () => {
                        this.cart.renderContents(response);
                      });
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                });
              }
            })
            .catch((e) => {
              console.error(e);
            })
            .finally(() => {
              this.submitButton.classList.remove('loading');
              if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner').classList.add('hidden');

              CartPerformance.measureFromEvent("add:user-action", evt);
            });
        });
      }

      parseSessionSku(sku) {
        const match = String(sku || '').match(/^SES-(\d{8})-(WHOLE|\d{4})$/i);
        if (!match) return null;
        const compact = match[1];
        const code = match[2].toUpperCase();
        return {
          date: `${compact.slice(0, 4)}-${compact.slice(4, 6)}-${compact.slice(6, 8)}`,
          code,
          isWhole: code === 'WHOLE',
          isEvening: code === '2030' || code === '2200' || code === '2330',
        };
      }

      async validateSessionBooking() {
        const productInfo = this.closest('product-info');
        const sessionCalendar = productInfo?.querySelector('session-calendar');
        if (!sessionCalendar) return true;

        const slot = sessionCalendar.querySelector('.session-slots__input:checked:not(.disabled)');
        if (!slot || !this.variantIdInput?.value) {
          this.handleErrorMessage('請選擇時段');
          sessionCalendar.querySelector('[data-session-cal-day], [data-session-day-label]')?.focus?.();
          return false;
        }

        const qtyInput = productInfo.querySelector('.session-qty .quantity__input');
        const qty = parseInt(qtyInput?.value, 10);
        const minQty = parseInt(qtyInput?.dataset.min || qtyInput?.min || '1', 10);
        const maxQty = parseInt(qtyInput?.max || qtyInput?.dataset.max, 10);
        if (!qtyInput || Number.isNaN(qty) || qty < 1) {
          this.handleErrorMessage('請選擇人數');
          qtyInput?.focus();
          return false;
        }
        if (qty < minQty) {
          this.handleErrorMessage(`此場次最少 ${minQty} 人`);
          qtyInput.focus();
          return false;
        }
        if (!Number.isNaN(maxQty) && qty > maxQty) {
          this.handleErrorMessage(`此場次最多 ${maxQty} 人`);
          qtyInput.focus();
          return false;
        }

        const wrap = slot.closest('[data-session-date]');
        const date = wrap?.dataset.sessionDate;
        const isWhole = wrap?.dataset.sessionWholeBar === 'true';
        if (date) {
          try {
            const cart = await fetch('/cart.js').then((response) => response.json());
            const conflict = (cart.items || []).some((item) => {
              const parsed = this.parseSessionSku(item.sku);
              if (!parsed || parsed.date !== date) return false;
              if (isWhole) return parsed.isEvening;
              return parsed.isWhole;
            });
            if (conflict) {
              this.handleErrorMessage(
                isWhole ? '購物車已有當晚散客時段，無法再訂包場' : '購物車已有當晚包場，無法再訂散客時段'
              );
              return false;
            }
          } catch (error) {
            console.error(error);
          }
        }

        return true;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent =
            this.submitButton.dataset.addToCartText || window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
